/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";

import {
  UserTrackingServiceApi,
  V1Decision,
  V1ExperimentLookupRequest,
  V1Platform,
} from "@flink/experiment";

import { EppoFeatureFlagsWithJson } from "app/types/flags";
import { config } from "config";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";

import { useHubSlug } from "./useHubSlug";

export type JsonFlagVariation = Record<string, unknown> | null;
type EppoJsonFlagsContext = {
  jsonFlags: JsonFlagVariation;
  isLoading: boolean;
  // true when the last fetch failed and no flags were ever loaded
  isError: boolean;
  refetch: () => Promise<void>;
};

// Add more as needed. Can be replaced in the future with automatic fetching of all JSON flags
// const JSON_FLAG_KEYS = Object.values(EppoFeatureFlagsWithJson);
const JSON_FLAG_KEYS = [EppoFeatureFlagsWithJson.STOCK_CHANGE_GUARDRAILS];

const parseVariation = (variation: string): unknown | null => {
  try {
    return JSON.parse(variation);
  } catch (err) {
    console.error("Failed to parse variation:", variation, err);
    return null;
  }
};

const transformDecisions = (decisions: V1Decision): JsonFlagVariation => {
  return Object.fromEntries(
    Object.entries(decisions).map(([key, { variation }]) => [
      key,
      variation ? parseVariation(variation) : null,
    ]),
  );
};

export const useEppoJsonFlags = (): EppoJsonFlagsContext => {
  const hubSlug = useHubSlug();
  const [jsonFlags, setJsonFlags] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchAllFeatureFlagDecisions = useCallback(async (): Promise<JsonFlagVariation> => {
    const api = new UserTrackingServiceApi({}, config.environment.EPPO_URL);
    const request: V1ExperimentLookupRequest = {
      userId: "not-used",
      meta: hubSlug ? { attributes: { hub_slug: hubSlug } } : {},
      flags: JSON_FLAG_KEYS,
      platform: V1Platform.EppoOps,
    };

    try {
      const response = await api.userTrackingServiceExperimentLookup(request);
      const { decisions } = response.data;
      if (!decisions) return null;
      return transformDecisions(decisions);
    } catch (error: any) {
      console.error("Error fetching all feature flags:", error?.response?.data ?? error);
      return null;
    }
  }, [hubSlug]);

  const fetchVariations = useCallback(async () => {
    if (!hubSlug) return;
    setIsLoading(true);
    try {
      const decisions = await fetchAllFeatureFlagDecisions();
      if (decisions) {
        setJsonFlags(decisions);
        setIsError(false);
      } else {
        // Keep the last successfully fetched flags, if any
        setIsError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllFeatureFlagDecisions, hubSlug]);

  useEffect(() => {
    fetchVariations();
  }, [fetchVariations]);

  useEffect(() => {
    const refetchIfIdle = () => {
      // Skip auto-refresh while a timed activity is in progress to avoid mid-task flag flips
      if (useTimedActivityStore.getState().timedActivityInstance) return;
      fetchVariations();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      refetchIfIdle();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", refetchIfIdle);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", refetchIfIdle);
    };
  }, [fetchVariations]);

  return { jsonFlags, isLoading, isError, refetch: fetchVariations };
};
